import React, { useMemo } from 'react';
import { ArrowDownLeft, ArrowUpRight, Download } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { formatDate, formatCurrencyNoDecimals } from '@/utils/formatters';
import { capitalCallNoticeFilename } from '../drawdown-notice-filename';
import { invoiceBreakdown } from '../invoice-breakdown';
import type { InvestorDrawdownSummary } from '../services/drawdowns-service';
import type { InvestorDistributionSummary } from '../services/distributions-service';
import { useDocumentDownload } from '../../documents/hooks/useDocuments';

type ActivityItem =
  | {
      type: 'distribution';
      date: string;
      distribution: InvestorDistributionSummary;
    }
  | { type: 'capital_call'; date: string; drawdown: InvestorDrawdownSummary };

interface TransactionActivityProps {
  paidDrawdowns: InvestorDrawdownSummary[];
  distributions: InvestorDistributionSummary[];
  title?: string;
}

export function TransactionActivity({
  paidDrawdowns,
  distributions,
  title,
}: TransactionActivityProps) {
  const items = useMemo(() => {
    const all: ActivityItem[] = [
      ...paidDrawdowns.map((d) => ({
        type: 'capital_call' as const,
        date: d.investor_allocation.last_payment_date || d.due_date,
        drawdown: d,
      })),
      ...distributions.map((d) => ({
        type: 'distribution' as const,
        date: d.distribution_date,
        distribution: d,
      })),
    ];
    return all.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [paidDrawdowns, distributions]);

  if (items.length === 0) return null;

  return (
    <section>
      {title && (
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
          {title}
        </h2>
      )}
      <div className="divide-y divide-border rounded-lg border border-border overflow-hidden bg-card">
        {items.map((item) =>
          item.type === 'distribution' ? (
            <DistributionRow
              key={`d-${item.distribution.id}`}
              distribution={item.distribution}
            />
          ) : (
            <DrawdownRow
              key={`c-${item.drawdown.id}`}
              drawdown={item.drawdown}
            />
          )
        )}
      </div>
    </section>
  );
}

function DistributionRow({
  distribution,
}: {
  distribution: InvestorDistributionSummary;
}) {
  return (
    <div className="relative flex items-center gap-3 py-3 px-4 hover:bg-muted/50 group transition-colors">
      <Link
        to={`/portal/transactions/distributions/${distribution.id}`}
        aria-label={`View distribution #${distribution.distribution_number} details`}
        className="absolute inset-0"
      />
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/10 flex-shrink-0">
        <ArrowDownLeft className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium text-sm">{distribution.fund_name}</span>
          <span className="text-xs text-muted-foreground">
            Distribution #{distribution.distribution_number}
          </span>
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {formatDate(distribution.distribution_date)}
        </div>
      </div>
      <div className="text-right flex-shrink-0 mr-1">
        <div className="font-semibold text-sm text-emerald-600 dark:text-emerald-400">
          +{formatCurrencyNoDecimals(distribution.investor_amount)}
        </div>
        <div className="text-xs text-emerald-600/70 dark:text-emerald-400/70">
          received
        </div>
      </div>
    </div>
  );
}

function paidNote(
  allocation: InvestorDrawdownSummary['investor_allocation']
): string {
  const paid =
    allocation.amount_paid === allocation.invoice_amount
      ? 'paid'
      : `paid ${formatCurrencyNoDecimals(allocation.amount_paid)}`;
  const breakdown = invoiceBreakdown(allocation);
  return breakdown ? `${breakdown} · ${paid}` : paid;
}

function DrawdownRow({ drawdown }: { drawdown: InvestorDrawdownSummary }) {
  const { downloadDocument } = useDocumentDownload();
  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (drawdown.notice_document_id) {
      downloadDocument(
        drawdown.notice_document_id,
        capitalCallNoticeFilename(drawdown.purpose)
      );
    }
  };

  const completionDate =
    drawdown.investor_allocation.last_payment_date || drawdown.due_date;
  const invoiceAmount = drawdown.investor_allocation.invoice_amount;

  return (
    <div className="relative flex items-center gap-3 py-3 px-4 hover:bg-muted/50 group transition-colors">
      <Link
        to={`/portal/transactions/drawdowns/${drawdown.id}`}
        aria-label={`View drawdown #${drawdown.drawdown_number} details`}
        className="absolute inset-0"
      />
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted flex-shrink-0">
        <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium text-sm">{drawdown.fund_name}</span>
          <span className="text-xs text-muted-foreground">
            Drawdown #{drawdown.drawdown_number}
          </span>
          {drawdown.purpose && (
            <span className="text-xs text-muted-foreground">
              · {drawdown.purpose}
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {formatDate(completionDate)}
        </div>
      </div>
      <div className="text-right flex-shrink-0 mr-1">
        <div className="font-semibold text-sm text-muted-foreground">
          &minus;{formatCurrencyNoDecimals(invoiceAmount)}
        </div>
        <div className="text-xs text-muted-foreground/70">
          {paidNote(drawdown.investor_allocation)}
        </div>
      </div>
      {drawdown.notice_document_id && (
        <div className="relative z-10 flex items-center gap-0.5 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={handleDownload}
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
